<script setup lang="ts">
    import { computed, shallowReactive } from 'vue'
    import { MonthYearHelpers, MonthYear } from '@common/types'
    import { MonthPickerDialogViewModel } from '@common/Components/MonthPickerDialog'
    import MonthPickerDialog from '@common/Components/MonthPickerDialog.vue'

    type CommitmentType = 'Total' | 'Committed' | 'Tentative'

    const props = defineProps<{
        canDisable: boolean

        isScheduleDependent: boolean

        defaultProjectStartDate: MonthYear | null
        startDateBoundary: MonthYear | null
        endDateBoundary: MonthYear | null

        applyBenefitsConfidenceFactor: boolean
        valueCommitmentType: CommitmentType
        showRiskAdjustment?: boolean
    }>()

    const emit = defineEmits<{
        (e: 'update:isScheduleDependent', value: boolean): void

        (e: 'update:defaultProjectStartDate', value: MonthYear | null): void
        (e: 'update:startDateBoundary', value: MonthYear | null): void
        (e: 'update:endDateBoundary', value: MonthYear | null): void

        (e: 'update:applyBenefitsConfidenceFactor', value: boolean): void
        (e: 'update:valueCommitmentType', value: CommitmentType): void
    }>()

    const isScheduleDependentModel = computed({
        get: () => props.isScheduleDependent,
        set: v => emit('update:isScheduleDependent', v)
    })

    const applyBenefitsConfidenceFactorModel = computed({
        get: () => props.applyBenefitsConfidenceFactor,
        set: v => emit('update:applyBenefitsConfidenceFactor', v)
    })

    const valueCommitmentTypeModel = computed({
        get: () => props.valueCommitmentType,
        set: v => emit('update:valueCommitmentType', v)
    })

    const showRiskAdjustment = computed(() => props.showRiskAdjustment ?? true)

    const startDateBoundaryLabel = computed(() => MonthYearHelpers.toMonthYearLongString(props.startDateBoundary))
    const endDateBoundaryLabel = computed(() => MonthYearHelpers.toMonthYearLongString(props.endDateBoundary))
    const defaultProjectStartDateLabel = computed(() => MonthYearHelpers.toMonthYearLongString(props.defaultProjectStartDate))

    const startDateBoundaryMonthPicker = shallowReactive(new MonthPickerDialogViewModel())
    const endDateBoundaryMonthPicker = shallowReactive(new MonthPickerDialogViewModel())
    const defaultProjectStartDateMonthPicker = shallowReactive(new MonthPickerDialogViewModel())

    // actions

    function showStartDateBoundaryMonthPicker()
    {
        startDateBoundaryMonthPicker.show(props.startDateBoundary, "Metric Start Date Boundary", false)
    }

    function showEndDateBoundaryMonthPicker()
    {
        endDateBoundaryMonthPicker.show(props.endDateBoundary, "Metric End Date Boundary", false)
    }

    function showDefaultProjectStartDateMonthPicker()
    {
        defaultProjectStartDateMonthPicker.show(props.defaultProjectStartDate, "Default Project Start Date", false)
    }

</script>

<template>
    <MonthPickerDialog :viewModel="startDateBoundaryMonthPicker" @update-date="emit('update:startDateBoundary', $event)" />
    <MonthPickerDialog :viewModel="endDateBoundaryMonthPicker" @update-date="emit('update:endDateBoundary', $event)" />
    <MonthPickerDialog :viewModel="defaultProjectStartDateMonthPicker" @update-date="emit('update:defaultProjectStartDate', $event)" />

    <fieldset class="border p-3 mt-4">
        <legend class="w-auto px-2 text-primary">Schedule-dependent Settings</legend>

        <!-- Toggle Schedule Dependent Settings -->
        <div v-if="canDisable" class="form-group mb-4">
            <div class="form-check">
                <input type="checkbox"
                       id="metricScheduleDependentToggle"
                       class="form-check-input"
                       v-model="isScheduleDependentModel" data-testid="schedule-dependent-toggle" />
                <label for="metricScheduleDependentToggle" class="form-check-label">
                    Enable schedule-dependent settings to configure time-sensitive and commitment-based metric adjustments.
                </label>
            </div>
        </div>

        <!-- Schedule Dependent Settings -->
        <div v-if="isScheduleDependent || !canDisable">

            <!-- Default Project Start Date -->
            <div class="form-group mb-4">
                <label for="metricDefaultProjectStartDate" class="form-label">Default Project Start Date:</label>
                <div class="input-group">
                    <input :value="defaultProjectStartDateLabel"
                           type="text"
                           class="form-control pointer bg-white"
                           placeholder="Optional..."
                           readonly
                           id="metricDefaultProjectStartDate"
                           @click="showDefaultProjectStartDateMonthPicker" data-testid="default-project-start-date" />
                    <div class="input-group-append">
                        <button class="btn btn-outline-secondary" type="button" @click="showDefaultProjectStartDateMonthPicker" data-testid="default-project-start-date-button">
                            <i class="fa-regular fa-calendar" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>
                <small class="form-text text-muted">
                    Used as the default start date for projects without one. Enables schedule-dependent calculations for unscheduled projects.
                    <strong>If neither a set nor default start date is provided, schedule-dependent metrics will have no value.</strong>
                </small>
            </div>

            <!-- Metric Boundaries -->
            <div class="form-row">
                <div class="form-group mb-4 col">
                    <label for="metricStartDateBoundary" class="form-label">Metric Start Date Boundary:</label>
                    <div class="input-group">
                        <input :value="startDateBoundaryLabel"
                               id="metricStartDateBoundary"
                               type="text"
                               class="form-control pointer bg-white"
                               placeholder="Optional..."
                               readonly
                               @click="showStartDateBoundaryMonthPicker" data-testid="start-date-boundary" />
                        <div class="input-group-append">
                            <button class="btn btn-outline-secondary" type="button" @click="showStartDateBoundaryMonthPicker" data-testid="start-date-boundary-button">
                                <i class="fa-regular fa-calendar" aria-hidden="true"></i>
                            </button>
                        </div>
                    </div>
                    <small class="form-text text-muted">
                        Includes only values after this date in calculations. If not set, values start from the project’s start date.
                    </small>
                </div>

                <div class="form-group mb-4 col">
                    <label for="metricEndDateBoundary" class="form-label">Metric End Date Boundary:</label>
                    <div class="input-group">
                        <input :value="endDateBoundaryLabel"
                               id="metricEndDateBoundary"
                               type="text"
                               class="form-control pointer bg-white"
                               placeholder="Optional..."
                               readonly
                               @click="showEndDateBoundaryMonthPicker" data-testid="end-date-boundary" />
                        <div class="input-group-append">
                            <button class="btn btn-outline-secondary" type="button" @click="showEndDateBoundaryMonthPicker" data-testid="end-date-boundary-button">
                                <i class="fa-regular fa-calendar" aria-hidden="true"></i>
                            </button>
                        </div>
                    </div>
                    <small class="form-text text-muted">
                        Includes only values before this date in calculations. If not set, values include all up to the project’s end date.
                    </small>
                </div>
            </div>

            <!-- Risk Adjustment -->
            <div class="form-row">
                <div class="form-group mb-4 col" v-if="showRiskAdjustment">
                    <label for="metricApplyBenefitsConfidenceFactor" class="form-label">Risk Adjustment:</label>
                    <select id="metricApplyBenefitsConfidenceFactor"
                            class="form-control"
                            v-model="applyBenefitsConfidenceFactorModel" data-testid="risk-adjustment">
                        <option :value="false">Disabled</option>
                        <option :value="true">Enabled</option>
                    </select>
                    <small class="form-text text-muted">
                        Applies a risk factor to adjust the metric value after the Benefits Confidence Date set at the portfolio level.
                        This setting is applicable only if the value source includes savings (benefits).
                    </small>
                </div>

                <!-- Commitment Adjustment -->
                <div class="form-group mb-4 col">
                    <label for="metricValueCommitmentType" class="form-label">Commitment Adjustment:</label>
                    <select id="metricValueCommitmentType"
                            class="form-control"
                            v-model="valueCommitmentTypeModel" data-testid="commitment-adjustment">
                        <option value="Total">Disabled</option>
                        <option value="Committed">Committed</option>
                        <option value="Tentative">Tentative</option>
                    </select>
                    <small class="form-text text-muted">
                        Adjusts the metric value based on the project commitment date:
                        <strong>Committed:</strong> Includes values before the commitment date, reflecting firmly committed values.
                        <strong>Tentative:</strong> Includes values after the commitment date, reflecting flexible or changeable plans.
                        <strong>Disabled:</strong> Ignores the commitment date, including all values.
                        <strong>Note:</strong> If no commitment date is set, all values are considered tentative.
                    </small>
                </div>
            </div>
        </div>
    </fieldset>
</template>